import React, { useEffect, useState } from "react";
import * as mammoth from "mammoth/mammoth.browser";
import { X, FileText, Loader2, Download, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* True-to-document preview: fetches the real optimized .docx and renders it
   (fonts/bold/tables/layout) with mammoth — no download required. */
export default function DocxPreview({ recordId, highlight, filename, downloadUrl, onClose }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const url = `${API}/download/${recordId}${highlight ? "?highlight_keywords=true" : ""}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Could not load document");
        const buf = await res.arrayBuffer();
        const result = await mammoth.convertToHtml(
          { arrayBuffer: buf },
          { styleMap: ["b => strong", "i => em", "u => u"] }
        );
        if (!cancelled) setHtml(result.value || "<p>(Empty document)</p>");
      } catch (e) {
        if (!cancelled) setError("Preview failed to render. You can still download the document.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recordId, highlight]);

  const cleanName = (filename || "Resume").replace(/\.[^/.]+$/, "");

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose} data-testid="docx-preview-overlay">
      <div className="bg-muted/40 dark:bg-muted/20 w-full max-w-4xl h-[90vh] rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden rz-scale-in"
        onClick={(e) => e.stopPropagation()} data-testid="docx-preview-modal">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 h-14 border-b border-border bg-background/80 backdrop-blur-xl shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-foreground shrink-0" />
            <span className="text-sm font-semibold text-foreground truncate">{cleanName}_optimized.docx</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors"
              data-testid="preview-download-btn"><Download size={13} /> Download</a>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors" data-testid="docx-preview-close"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 sm:p-8">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="preview-loading">
              <Loader2 size={28} className="animate-spin" />
              <span className="text-sm">Rendering your document…</span>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground px-6 text-center" data-testid="preview-error">
              <AlertTriangle size={28} className="text-amber-500" />
              <span className="text-sm">{error}</span>
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground"><Download size={14} /> Download instead</a>
            </div>
          ) : (
            <div className="mx-auto bg-white text-black shadow-xl rounded-sm max-w-[820px] min-h-full px-8 sm:px-14 py-10 docx-render"
              data-testid="docx-preview-content" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    </div>
  );
}
